// MA-66 content-wiring slice — approved AI-image URL lookup.
// scripts/aiImages/resolveApprovedImages.mjs writes this index offline (a
// human/CI step, not build-wired) from the reviewer-approved + uploaded
// subset of the AI-image manifest. It shipped committed as `{}` at first —
// no image had been approved anywhere yet (see the script's header) — so
// every lookup below resolved to null until a real batch was generated,
// reviewed, uploaded, and the index regenerated.
//
// MA-492 Slice 5b/wiring: the index now carries the first real batch — 48
// reviewer-approved + uploaded controlledSentences (CS) decode-cloze target
// words (keyed by the bare WORD, e.g. "band", NOT by item id — a single
// word can back many CS item occurrences). illustrationUrlForWord below is
// the CS-side lookup; illustrationUrlForItem (id-keyed) remains for the
// semanticRelation/grammarItem callers that resolved earlier and is
// untouched by this slice.
import approvedImages from "../data/generated/approvedImages.json";

interface ApprovedImageEntry {
  illustrationUrl: string;
  hashKey: string;
}

const INDEX: Readonly<Record<string, ApprovedImageEntry>> = approvedImages;

/** The approved illustration URL for a content-item id, or null when none
 *  has been approved+uploaded (the case for every real item today). */
export function illustrationUrlForItem(
  itemId: string | undefined,
  index: Readonly<Record<string, ApprovedImageEntry>> = INDEX,
): string | null {
  if (!itemId) return null;
  return index[itemId]?.illustrationUrl ?? null;
}

/** The approved illustration URL for a target WORD (controlledSentences'
 *  correctAnswer), or null when the word is undefined/empty/unapproved.
 *  Trims + lowercases before lookup — the manifest keys words in lowercase
 *  (see scripts/aiImages/csWordImageTargets.mjs), while authored content
 *  may carry a differently-cased correctAnswer. */
export function illustrationUrlForWord(
  word: string | undefined,
  index: Readonly<Record<string, ApprovedImageEntry>> = INDEX,
): string | null {
  if (!word) return null;
  const key = word.trim().toLowerCase();
  if (!key) return null;
  return index[key]?.illustrationUrl ?? null;
}
